// Utility functions for exporting data to various formats

export interface ExportData {
  headers: string[];
  rows: (string | number)[][];
  filename: string;
  title?: string;
}

// ── Output encoding ─────────────────────────────────────────────────────────
// Report cells can carry text that originated on a QR sticker (unmapped
// checkpoint names, raw payloads). Every export encodes it for its format:
//   HTML/PDF  → HTML-escaped, so a payload like <img onerror=…> stays text
//               (the PDF view is a same-origin window that can read the
//               session token);
//   CSV/TSV   → a leading = + - @ (or tab/CR) is neutralised with a quote so
//               Excel/Sheets never evaluate a cell as a formula.
export const escapeHtml = (v: unknown): string =>
  String(v ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));

const neutraliseFormula = (v: unknown): string => {
  const s = String(v ?? '');
  return /^[=+\-@\t\r]/.test(s) ? `'${s}` : s;
};

const csvCell = (v: unknown): string => {
  const s = typeof v === 'number' ? String(v) : neutraliseFormula(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const tsvCell = (v: unknown): string =>
  (typeof v === 'number' ? String(v) : neutraliseFormula(v)).replace(/[\t\r\n]+/g, ' ');

// Convert data to CSV format and trigger download
export const exportToCSV = (data: ExportData): void => {
  const csvContent = [
    data.headers.map(csvCell).join(','),
    ...data.rows.map(row => row.map(csvCell).join(','))
  ].join('\n');

  downloadFile(csvContent, `${data.filename}.csv`, 'text/csv');
};

// Convert data to TXT format and trigger download (Tab-aligned for readability)
export const exportToTXT = (data: ExportData): void => {
  const columnWidths = data.headers.map((header, i) => {
    const maxRowWidth = Math.max(...data.rows.map(row => String(row[i]).length));
    return Math.max(header.length, maxRowWidth) + 2;
  });

  const separator = columnWidths.map(w => '-'.repeat(w)).join('+');

  const txtContent = [
    data.title || 'Security Patrol Report',
    `Generated on: ${new Date().toLocaleString()}`,
    '',
    data.headers.map((h, i) => h.padEnd(columnWidths[i])).join('|'),
    separator,
    ...data.rows.map(row =>
      row.map((cell, i) => String(cell).padEnd(columnWidths[i])).join('|')
    )
  ].join('\n');

  downloadFile(txtContent, `${data.filename}.txt`, 'text/plain');
};

// Convert data to TSV format and trigger download
export const exportToTSV = (data: ExportData): void => {
  const tsvContent = [
    data.headers.map(tsvCell).join('\t'),
    ...data.rows.map(row => row.map(tsvCell).join('\t'))
  ].join('\n');

  downloadFile(tsvContent, `${data.filename}.tsv`, 'text/tab-separated-values');
};

// Generate basic HTML report and trigger download
export const exportToHTML = (data: ExportData): void => {
  const htmlContent = `
<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <title>${escapeHtml(data.title || data.filename)}</title>
    <style>
        body { font-family: Arial, sans-serif; margin: 20px; }
        h1 { color: #A0826D; margin-bottom: 20px; }
        table { border-collapse: collapse; width: 100%; margin-top: 20px; }
        th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
        th { background-color: #f8f9fa; font-weight: bold; }
        tr:nth-child(even) { background-color: #f8f9fa; }
        .meta { color: #666; font-size: 12px; margin-bottom: 20px; }
    </style>
</head>
<body>
    <h1>${escapeHtml(data.title || 'Security Patrol Report')}</h1>
    <div class="meta">Generated on: ${new Date().toLocaleString()}</div>
    <table>
        <thead>
            <tr>
                ${data.headers.map(header => `<th>${escapeHtml(header)}</th>`).join('')}
            </tr>
        </thead>
        <tbody>
            ${data.rows.map(row =>
    `<tr>${row.map(cell => `<td>${escapeHtml(cell)}</td>`).join('')}</tr>`
  ).join('')}
        </tbody>
    </table>
</body>
</html>`;

  downloadFile(htmlContent, `${data.filename}.html`, 'text/html');
};

// Generate JSON export and trigger download
export const exportToJSON = (data: ExportData): void => {
  const jsonData = {
    title: data.title || 'Security Patrol Report',
    generated: new Date().toISOString(),
    headers: data.headers,
    data: data.rows.map(row => {
      const obj: Record<string, string | number> = {};
      data.headers.forEach((header, index) => {
        obj[header] = row[index];
      });
      return obj;
    })
  };

  const jsonContent = JSON.stringify(jsonData, null, 2);
  downloadFile(jsonContent, `${data.filename}.json`, 'application/json');
};

// Generate a PDF-friendly version and trigger print
export const exportToPDF = (data: ExportData): void => {
  const htmlContent = `
<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <title>${escapeHtml(data.title || data.filename)}</title>
    <style>
        body { font-family: Arial, sans-serif; margin: 40px; color: #333; }
        h1 { color: #012169; margin-bottom: 20px; border-bottom: 2px solid #f2a900; padding-bottom: 10px; }
        table { border-collapse: collapse; width: 100%; margin-top: 30px; }
        th, td { border: 1px solid #ddd; padding: 12px 8px; text-align: left; }
        th { background-color: #f8f9fa; font-weight: bold; color: #012169; }
        tr:nth-child(even) { background-color: #f9fafb; }
        .meta { color: #666; font-size: 14px; margin-bottom: 30px; display: flex; justify-content: space-between; }
        .footer { margin-top: 50px; font-size: 12px; color: #999; text-align: center; border-top: 1px solid #eee; padding-top: 20px; }
        @media print {
            button { display: none; }
            body { margin: 20mm; }
        }
    </style>
</head>
<body>
    <h1>${escapeHtml(data.title || 'Security Patrol Report')}</h1>
    <div class="meta">
        <span>Generated on: ${new Date().toLocaleString()}</span>
        <span>Emory Healthcare Security</span>
    </div>
    <table>
        <thead>
            <tr>
                ${data.headers.map(header => `<th>${escapeHtml(header)}</th>`).join('')}
            </tr>
        </thead>
        <tbody>
            ${data.rows.map(row =>
    `<tr>${row.map(cell => `<td>${escapeHtml(cell)}</td>`).join('')}</tr>`
  ).join('')}
        </tbody>
    </table>
    <div class="footer">
        Confidential - For Authorized Use Only
    </div>
</body>
</html>`;

  const printWindow = window.open('', '_blank');
  if (printWindow) {
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    // Wait for content to load before printing
    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
      // Most browsers will block auto-closing if print is pending, 
      // but this is a good cleanup attempt
      // printWindow.close();
    }, 500);
  } else {
    alert("Please allow popups to generate PDF reports.");
  }
};

// Helper function to trigger file download
const downloadFile = (content: string, filename: string, mimeType: string): void => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Clean up the URL object
  setTimeout(() => URL.revokeObjectURL(url), 100);
};

// Generate report data for different report types
export const generateGuardPerformanceData = (guards: any[]): ExportData => ({
  // One row per officer (the person signed in on the device), with the scans
  // made on each device.
  headers: ['Officer', 'N-Number', 'Scans', 'Checkpoints Scanned', 'Devices Used (scans)', 'Last Scan'],
  rows: guards.map(guard => [
    guard.name,
    guard.id || '',
    guard.completed,
    guard.checkpoints ?? '',
    Array.isArray(guard.devices) ? guard.devices.map((d: any) => `${d.device}: ${d.scans}`).join('; ') : '',
    guard.lastActive ? new Date(guard.lastActive).toLocaleString() : ''
  ]),
  filename: `officer-activity-${new Date().toISOString().split('T')[0]}`,
  title: 'Officer Activity Report'
});

export const generateComplianceData = (data: any[]): ExportData => ({
  headers: ['Period', 'Checkpoints Completed', 'Checkpoints Missed', 'Compliance Rate (%)'],
  rows: data.map(item => [
    item.period,
    item.completed,
    item.missed,
    item.compliance
  ]),
  filename: `compliance-report-${new Date().toISOString().split('T')[0]}`,
  title: 'Compliance Report'
});

export const generateLocationData = (locations: any[]): ExportData => ({
  headers: ['Location', 'Total Scans', 'Last Scanned'],
  rows: locations.map(location => [
    location.location,
    location.scans,
    location.lastScan ? new Date(location.lastScan).toLocaleString() : ''
  ]),
  filename: `location-analysis-${new Date().toISOString().split('T')[0]}`,
  title: 'Location Analysis Report'
});

export const generateActivityData = (activities: any[]): ExportData => ({
  headers: ['Timestamp', 'Guard Name', 'Guard ID', 'Checkpoint', 'Location', 'Status', 'Route', 'Notes'],
  rows: activities.map(activity => [
    new Date(activity.timestamp).toLocaleString(),
    activity.guardName,
    activity.guardId,
    activity.checkpoint,
    activity.location ? `${activity.location.lat}, ${activity.location.lng}` : 'Unknown',
    activity.status,
    activity.route,
    activity.notes || ''
  ]),
  filename: `activity-report-${new Date().toISOString().split('T')[0]}`,
  title: 'Live Activity Report'
});

export const generateAlertData = (alerts: any[]): ExportData => ({
  headers: ['Alert ID', 'Type', 'Priority', 'Title', 'Description', 'Guard', 'Location', 'Status', 'Timestamp'],
  rows: alerts.map(alert => [
    alert.id,
    alert.type,
    alert.priority,
    alert.title,
    alert.description,
    alert.guardName || 'N/A',
    alert.location || 'N/A',
    alert.status,
    new Date(alert.timestamp).toLocaleString()
  ]),
  filename: `alerts-report-${new Date().toISOString().split('T')[0]}`,
  title: 'Security Alerts Report'
});

// Export format options
export const EXPORT_FORMATS = [
  { value: 'csv', label: 'CSV (Excel Compatible)', icon: '📊' },
  { value: 'tsv', label: 'TSV (Tab Separated)', icon: '📋' },
  { value: 'json', label: 'JSON (Developer)', icon: '💾' },
  { value: 'html', label: 'HTML (Web Report)', icon: '🌐' },
  { value: 'pdf', label: 'PDF (Print Document)', icon: '📄' },
  { value: 'txt', label: 'Plain Text (Formatted)', icon: '📝' },
] as const;

export type ExportFormat = typeof EXPORT_FORMATS[number]['value'];